import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
import DocumentList from '../global/DocumentList';
import Container from "../global/Container";
import Context from '../../context';
import Summary from "../Summary";
import { useLocation } from "react-router-dom";
import DocumentViewer from "../global/DocumentViewer";
import NotFoundScreen from "../global/errorPages/NotFoundScreen";
import AddIcon from "@mui/icons-material/Add";
import MyDocumentManager from "../global/DocumentList/MyDocumentManager";
import { getEwaBackPath } from "../../libraries/ewa/helpers";
import { BACK_NAVIGATION_TYPE } from "../../helpers/constants";

const Documents = () => {
  const { itin, viewMode, navMenuItems, t } = useContext(Context);
  const [docScreenName, setDocScreenName] = useState(null);
  const [cancelUploadSignal, setCancelUploadSignal] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const { pathname } = useLocation();
  const urlParts = useMemo(() => pathname.split('/'), [pathname]);
  const finalPart = urlParts[urlParts.length - 1];
  const fileInputRef = useRef(null);

  const closePath = useMemo(() => {
    const ewaBackPath = getEwaBackPath();
    return (ewaBackPath && pathname === ewaBackPath?.entryPath)
      ? BACK_NAVIGATION_TYPE
      : isUploadOpen
        ? pathname
        : undefined;
  }, [pathname, isUploadOpen]);

  const getFiles = useCallback(() => {
    fileInputRef.current.click();
  }, []);

  const isNavItem = !!navMenuItems.find(item => item.initialPath === pathname);
  const docFolder = isNavItem
    ? itin.documents?.find(doc => doc.pathName === finalPart)
    : itin.documents?.find(doc => doc.id === parseInt(finalPart) && doc.type === 'folder');

  const handleBackButtonClick = () => {
    if (isUploadOpen) setCancelUploadSignal(true);
  };

  useEffect(() => {
    if (docFolder) {
      if (isNavItem) {
        const menuItemLabel = itin.menu.find(x => x.segueId === docFolder.id)?.label;
        setDocScreenName(menuItemLabel);
      } else {
        setDocScreenName(docFolder?.name || null);
      }
    }

    return () => setDocScreenName(null);
  }, [docFolder, isNavItem, itin.menu]);

  const getMobileHeaderButtonRightConfig = () => {
    if (!docFolder?.isEditable || isUploadOpen) return null;
    return {
      className: "upload-new-file",
      onClick: getFiles,
      icon: <AddIcon className="upload-new-file-icon" />
    };
  };

  if (urlParts.includes('document_settings')) {
    const document = itin.documents.find(doc => doc.id === parseInt(finalPart));
    return document ? <MyDocumentManager document={document} /> : <NotFoundScreen isDocument />
  }

  if (!docFolder) {
    const docData = itin.documents.find(doc => doc.id === parseInt(finalPart));
    return docData ? <DocumentViewer docData={docData} /> : <NotFoundScreen isDocument />
  }

  const data = docFolder.entries.map(id => itin.documents.find(doc => doc.id === id));

  return (
    <Container
      className="container-documents"
      mainElementClassName={`documents documents-${viewMode}`}
      docScreenName={!isUploadOpen ? docScreenName : t('document_settings_add_document', { lng: itin.language })}
      closePath={closePath}
      mobileHeaderButtonRight={docFolder?.isEditable ? getMobileHeaderButtonRightConfig() : undefined}
      backButtonClickFn={handleBackButtonClick}
    >
      {docFolder?.displayMode === 'gallery'
        ? <Summary type="gallery" isDocuments data={data} showTitle={!isNavItem} docScreenName={docScreenName} />
        : <DocumentList
          section={docFolder?.isEditable ? "my-documents" : "documents"}
          folderId={docFolder.id}
          data={data}
          showTitle={!isNavItem}
          docScreenName={docScreenName}
          fileInputRef={docFolder?.isEditable ? fileInputRef : undefined}
          getFiles={docFolder?.isEditable ? getFiles : undefined}
          cancelUploadSignal={cancelUploadSignal}
          setCancelUploadSignal={setCancelUploadSignal}
          isUploadOpen={isUploadOpen}
          setIsUploadOpen={setIsUploadOpen}
        />
      }
    </Container>
  )
};

export default Documents;